import { useCallback, useEffect, useRef, useState } from 'react';
import { currentLanguage, type Language, SPEECH_LANG } from '@/i18n';
import { newId } from '@/lib/id';
import {
  type Clarification,
  type ClarificationOption,
  type Command,
  type Draft,
  EMPTY_CONTEXT,
  endsInAnamnesis,
  finishDictation,
  type ParseContext,
  parseDictation,
  readSections,
  readSpokenPrice,
  toLatin,
} from '../parser';
import { appendTranscriptLine, mergeQuestions, planSpeechChunk } from './dictationLogic';
import { createFinalFilter, type FinalResult } from './finalFilter';
import { useSpeechRecognition } from './useSpeechRecognition';

/** Everything said or typed since the chart opened (or the language changed). */
export type DictationSession = {
  clientId: string;
  /** BCP 47 speech language, e.g. sr-RS. */
  lang: string;
  startedAt: string;
  lines: string[];
};

type Options = {
  /** `sessionId`: the clientId of the dictation the drafts came from. */
  onDrafts: (drafts: Draft[], sessionId: string) => void;
  onCommand: (command: Command) => void;
  /** A price was said with its currency ("3000 dinara"), in RSD. */
  onPrice: (price: number) => void;
  /** The session's text changed; persist it. */
  onSession: (session: DictationSession) => void;
  /** "anamneza …" (words to add to the anamnesis) and "dijagnoza K02.1" (codes). */
  onRecord?: (part: VisitRecordPart) => void;
};

/** What a piece of dictation adds to the visit's protocol record (#23). */
export type VisitRecordPart = { anamnesis?: string; diagnoses?: string[] };

/** Speech (or typed text) → parser → drafts and questions, plus the full transcript. */
export function useDictation({ onDrafts, onCommand, onPrice, onSession, onRecord }: Options) {
  const ctx = useRef<ParseContext>(EMPTY_CONTEXT);
  const [questions, setQuestions] = useState<Clarification[]>([]);
  const handlers = useRef({ onDrafts, onCommand, onPrice, onSession, onRecord });
  handlers.current = { onDrafts, onCommand, onPrice, onSession, onRecord };

  // Defaults to the user's language; switchable per chart before dictating.
  const [lang, setLang] = useState<Language>(currentLanguage);
  const sessionRef = useRef<DictationSession | null>(null);
  const [session, setSession] = useState<DictationSession | null>(null);

  const prevCtx = useRef<ParseContext>(EMPTY_CONTEXT);
  /** The last chunk only named a tooth (no entry, question or command). */
  const lastOnlyAwaited = useRef(false);
  const filter = useRef(createFinalFilter());

  /** Applies `update` to the session's lines (opening one if needed) and reports it. */
  const record = useCallback(
    (update: (lines: readonly string[]) => string[]): DictationSession => {
      const current = sessionRef.current ?? {
        clientId: newId(),
        lang: SPEECH_LANG[lang],
        startedAt: new Date().toISOString(),
        lines: [],
      };
      const next = { ...current, lines: update(current.lines) };
      sessionRef.current = next;
      setSession(next);
      handlers.current.onSession(next);
      return next;
    },
    [lang]
  );

  /** The utterance being spoken ended inside an anamnesis: its next words continue it. */
  const inAnamnesis = useRef(false);

  /**
   * The anamnesis and diagnosis codes in `text` go to the visit's record;
   * returns the rest, for the tooth parser, and whether anything was taken.
   * `continues`: `text` is more words of an utterance inside an anamnesis.
   */
  const takeRecord = useCallback(
    (text: string, continues: boolean): { rest: string; took: boolean } => {
      const sections = readSections(text, lang, continues);
      inAnamnesis.current = endsInAnamnesis(text, lang, continues);
      const { anamnesis, diagnoses } = sections;
      if (anamnesis || diagnoses.length) {
        handlers.current.onRecord?.({
          ...(anamnesis ? { anamnesis } : {}),
          ...(diagnoses.length ? { diagnoses } : {}),
        });
      }
      return { rest: sections.rest, took: !!anamnesis || diagnoses.length > 0 };
    },
    [lang]
  );

  /**
   * Parse `text` from context `from` as part of dictation `sessionId`. The price
   * is read from the `whole` utterance: its currency may come in a later piece.
   */
  const run = useCallback(
    (text: string, from: ParseContext, sessionId: string, confidence?: number, whole = text) => {
      prevCtx.current = from;
      const result = parseDictation(text, from, { speechConfidence: confidence, lang });
      ctx.current = result.context;
      lastOnlyAwaited.current =
        !(result.drafts.length || result.clarifications.length || result.commands.length) &&
        result.price === null &&
        !!result.context.awaitingTeeth;
      if (result.drafts.length) handlers.current.onDrafts(result.drafts, sessionId);
      const recorded = new Set(result.drafts.map((d) => d.tooth));
      setQuestions((q) => mergeQuestions(q, result.clarifications, recorded));
      for (const c of result.commands) handlers.current.onCommand(c);
      const price = readSpokenPrice(whole, lang);
      if (price !== null) handlers.current.onPrice(price);
    },
    [lang]
  );

  // Serbian recognition may answer in Cyrillic; keep transcripts in Latin script.
  const latin = useCallback((raw: string) => (lang === 'sr' ? toLatin(raw) : raw), [lang]);

  /**
   * A speech result. Android repeats a growing utterance ("11", "11 hiányzik"):
   * repeats are ignored; growth after a bare tooth is read again as a whole
   * from before that tooth ("17" + "17 18 hiányzik" → both teeth); other growth
   * parses only the added words, so nothing is recorded twice.
   */
  const handle = useCallback(
    (raw: string, confidence?: number) => {
      const f: FinalResult = filter.current(latin(raw));
      const plan = planSpeechChunk(f, {
        current: ctx.current,
        previous: prevCtx.current,
        lastOnlyAwaited: lastOnlyAwaited.current,
      });
      if (!plan) return;
      const s = record((lines) => appendTranscriptLine(lines, f));
      const { rest, took } = takeRecord(plan.text, f.kind === 'extends' && inAnamnesis.current);
      run(rest, plan.from, s.clientId, confidence, plan.whole);
      // The utterance said more than a tooth: it is not read again from its start.
      if (took) lastOnlyAwaited.current = false;
    },
    [latin, record, run, takeRecord]
  );

  const finish = useCallback(() => {
    const pending = finishDictation(ctx.current, lang);
    if (pending.length) setQuestions((q) => mergeQuestions(q, pending, new Set()));
    ctx.current = { ...ctx.current, awaitingTeeth: null, awaitingTranscript: null };
  }, [lang]);

  const speech = useSpeechRecognition({ lang: SPEECH_LANG[lang], onFinal: handle });

  // When listening stops, a tooth still waiting for its finding becomes a question.
  const wasListening = useRef(false);
  useEffect(() => {
    if (wasListening.current && !speech.listening) finish();
    wasListening.current = speech.listening;
  }, [speech.listening, finish]);

  const submitText = useCallback(
    (text: string) => {
      const typed = latin(text.trim());
      if (!typed) return;
      const s = record((lines) => [...lines, typed]);
      run(takeRecord(typed, false).rest, ctx.current, s.clientId);
      inAnamnesis.current = false;
      finish();
    },
    [latin, record, run, finish, takeRecord]
  );

  const resolve = useCallback((id: string, option: ClarificationOption) => {
    setQuestions((q) => q.filter((x) => x.id !== id));
    const sessionId = sessionRef.current?.clientId;
    if (option.drafts.length && sessionId) {
      handlers.current.onDrafts(option.drafts, sessionId);
      ctx.current = { ...ctx.current, lastTeeth: [option.drafts[0].tooth] };
    }
  }, []);

  const dismiss = useCallback((id: string) => {
    setQuestions((q) => q.filter((x) => x.id !== id));
  }, []);

  const reset = useCallback(() => {
    sessionRef.current = null;
    setSession(null);
    setQuestions([]);
    ctx.current = EMPTY_CONTEXT;
    prevCtx.current = EMPTY_CONTEXT;
    lastOnlyAwaited.current = false;
    inAnamnesis.current = false;
    filter.current = createFinalFilter();
  }, []);

  /** A new language starts a new dictation: one transcript has one language. */
  const changeLanguage = useCallback(
    (next: Language) => {
      if (next === lang || speech.listening) return;
      reset();
      setLang(next);
    },
    [lang, speech.listening, reset]
  );

  return {
    speech,
    lang,
    changeLanguage,
    session,
    lines: session?.lines ?? [],
    questions,
    submitText,
    resolve,
    dismiss,
    clear: reset,
  };
}
